import React from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { CheckCircle2, Calendar, Clock, MapPin, Phone, ArrowRight, Truck } from 'lucide-react';
import { useApp } from '../context/AppContext';
import DesktopHeader from '../components/DesktopHeader';

const BookingConfirm = () => {
  const nav = useNavigate();
  const { bookingId } = useParams();
  const { bookings } = useApp();
  const booking = bookings.find((b) => b.id === bookingId);

  if (!booking) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center p-6">
        <p className="text-neutral-600">Booking not found</p>
        <button onClick={() => nav('/home')} className="mt-3 text-[#2E7D32] font-semibold">Go to Home</button>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-neutral-50 pb-8">
      <DesktopHeader />

      <div className="max-w-[900px] mx-auto px-5 md:px-6 pt-6 md:pt-10">
        <div className="bg-white border border-neutral-200 rounded-2xl p-6 md:p-8 text-center">
          <div className="w-20 h-20 rounded-full bg-[#E8F5E9] flex items-center justify-center mx-auto">
            <CheckCircle2 className="w-10 h-10 text-[#2E7D32]" />
          </div>
          <h1 className="mt-4 text-2xl md:text-3xl font-extrabold text-neutral-900">Booking Confirmed!</h1>
          <p className="text-sm text-neutral-600 mt-1">Your cleaner will arrive at the scheduled time</p>
          <p className="mt-3 text-xs text-neutral-500">Booking ID: <span className="font-bold text-neutral-800">{booking.id}</span></p>
        </div>

        <div className="mt-4 grid md:grid-cols-2 gap-4">
          <div className="bg-white border border-neutral-200 rounded-xl p-4 space-y-3">
            <p className="font-bold text-sm mb-2">Booking details</p>
            <InfoRow icon={Calendar} label="Date" value={new Date(booking.date).toLocaleDateString('en-US', { weekday: 'long', day: 'numeric', month: 'long' })} />
            <InfoRow icon={Clock} label="Time slot" value={booking.slot} />
            <InfoRow icon={MapPin} label="Service address" value={booking.address} />
            <InfoRow icon={Phone} label="Support" value="+91 90000 12345" />
          </div>

          <div className="bg-white border border-neutral-200 rounded-xl p-4">
            <p className="font-bold text-sm mb-3">Services booked</p>
            <div className="space-y-2">
              {booking.items.map((i) => (
                <div key={i.id} className="flex items-center gap-3">
                  <img src={i.image} alt="" className="w-12 h-12 rounded-lg object-cover" />
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-semibold truncate">{i.name}</p>
                    <p className="text-xs text-neutral-500">₹{i.price} × {i.qty}</p>
                  </div>
                  <p className="text-sm font-bold text-[#EA580C]">₹{i.price * i.qty}</p>
                </div>
              ))}
            </div>
            <div className="border-t border-dashed border-neutral-200 my-3" />
            <div className="flex justify-between font-bold"><span>Total paid</span><span className="text-[#EA580C]">₹{booking.total}</span></div>
            <p className="text-xs text-neutral-500 mt-1">Payment: {booking.payment === 'cash' ? 'Cash on service' : 'UPI (after service)'}</p>
          </div>
        </div>

        <div className="mt-4 bg-gradient-to-r from-[#E8F5E9] to-white border border-[#C8E6C9] rounded-xl p-4 flex items-center gap-3">
          <div className="w-10 h-10 rounded-full bg-white flex items-center justify-center">
            <Truck className="w-5 h-5 text-[#2E7D32]" />
          </div>
          <div className="flex-1">
            <p className="font-bold text-sm">Live tracking will be available</p>
            <p className="text-xs text-neutral-600">You'll get a notification when the cleaner is on the way.</p>
          </div>
        </div>

        <div className="mt-4 grid grid-cols-2 gap-3">
          <button onClick={() => nav('/bookings')} className="py-3 rounded-xl border border-neutral-200 bg-white font-semibold text-sm hover:bg-neutral-50 transition-colors">View bookings</button>
          <button onClick={() => nav('/home')} className="py-3 rounded-xl bg-[#2E7D32] hover:bg-[#276D2A] text-white font-semibold text-sm flex items-center justify-center gap-1 transition-colors">Continue <ArrowRight className="w-4 h-4" /></button>
        </div>
      </div>
    </div>
  );
};

const InfoRow = ({ icon: Icon, label, value }) => (
  <div className="flex items-start gap-3">
    <div className="w-9 h-9 rounded-full bg-[#E8F5E9] flex items-center justify-center flex-shrink-0">
      <Icon className="w-4 h-4 text-[#2E7D32]" />
    </div>
    <div>
      <p className="text-[11px] text-neutral-500 uppercase tracking-wide">{label}</p>
      <p className="text-sm font-semibold text-neutral-900">{value}</p>
    </div>
  </div>
);

export default BookingConfirm;
